/**
 * COP Dashboard API Service
 * Handles real-time device location data
 */

export interface DeviceLocationStatus {
  device_id: string;
  entity_id?: string;
  entity_type?: string;
  device_type?: string;
  course_id?: string;
  latitude: number;
  longitude: number;
  timestamp: string;
  speed?: number;
  /** Roster collector ids — how DFP names the same radio. */
  phone_app_id?: string | null;
  wardriver_id?: string | null;
  watch_app_id?: string | null;
  vehicle_beacon_id?: string | null;
  drone_app_id?: string | null;
  bearing?: number;
  battery_level?: number;
  connection_status?: string;
  last_seen_minutes_ago?: number;
  is_stale?: boolean;  // True if device hasn't reported in > 15 minutes
  is_streaming?: boolean;  // LiveKit A/V currently advertised

  // Aircraft only — inputs to the camera footprint projection.
  // altitude_agl_m is height above the takeoff point, not MSL or terrain.
  altitude_agl_m?: number;
  gimbal_pitch_deg?: number;
  gimbal_yaw_deg?: number;
  gimbal_yaw_rel_deg?: number;
  gimbal_roll_deg?: number;
  gimbal_mode?: string;
  hfov_deg?: number;
  vfov_deg?: number;
  is_flying?: boolean;

  // Aircraft flight state. Not inputs to the footprint — this is what the
  // operator reads off the aircraft HUD.
  vertical_speed_ms?: number;
  satellite_count?: number;
  home_latitude?: number;
  home_longitude?: number;
  platform?: string;
  session_id?: string;
}

export interface COPDeviceResponse {
  total_devices: number;
  active_devices: number;
  devices: DeviceLocationStatus[];
  last_updated: string;
}

export interface DeviceStatusEntry {
  device_id: string;
  entity_id?: string;
  entity_type?: string;
  course_id?: string;
  device_type?: string;
  status_type: string;
  status_state: string;
  timestamp: string;
  latitude?: number;
  longitude?: number;
  device_reference_id?: number;
  minutes_ago: number;
}

export interface DeviceStatusResponse {
  status_entries: DeviceStatusEntry[];
  count: number;
}

/** Ground area an aircraft's camera observed over a time window. */
export interface DroneCoverageResponse {
  device_id: string;
  start_time: string;
  end_time: string;
  interval_seconds: number;
  sample_count: number;
  /** True when the window hit the server-side sample cap and was cut short. */
  truncated: boolean;
  area_m2: number;
  area_acres: number;
  /** Unioned footprint, or null when no sample had usable telemetry. */
  coverage: GeoJSON.Polygon | GeoJSON.MultiPolygon | null;
}

class COPApiService {
  private baseUrl: string = process.env.REACT_APP_API_URL || '/api';

  /**
   * Fetch current device locations for COP Dashboard
   */
  async getDeviceLocations(
    active_only: boolean = false
  ): Promise<COPDeviceResponse> {
    const queryParams = new URLSearchParams();

    if (active_only === true) {
      queryParams.append('active_only', active_only.toString());
    }

    const url = `${this.baseUrl}/pathfinder-postgres/cop-devices${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching COP device locations:', error);
      throw error;
    }
  }

  /**
   * Fetch the accumulated ground area an aircraft's camera observed.
   *
   * The union is computed server-side: a ten minute flight at 10 Hz is
   * thousands of polygons, which is not something to ship to the browser.
   */
  async getDroneCoverage(
    deviceId: string,
    startTime: Date,
    endTime: Date,
    intervalSeconds: number = 1
  ): Promise<DroneCoverageResponse> {
    const params = new URLSearchParams({
      device_id: deviceId,
      start_time: startTime.toISOString(),
      end_time: endTime.toISOString(),
      interval_seconds: String(intervalSeconds),
    });

    const response = await fetch(`${this.baseUrl}/drone-coverage?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('access_token') || ''}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Fetch available courses from database
   */
  async getCourses(): Promise<string[]> {
    try {
      const response = await fetch(`${this.baseUrl}/pathfinder-postgres/courses`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}` //????
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data.courses || [];
    } catch (error) {
      console.error('[COP Filters Service] Error fetching courses:', error);
      return [];
    }
  }

  /**
   * Fetch device status entries - most recent status for each device/status_type
   */
  async getDeviceStatus(courseId?: string, deviceType?: string, entityType?: string): Promise<DeviceStatusResponse> {
    try {
      const queryParams = new URLSearchParams();
      
      if (courseId && courseId !== 'All Courses') {
        queryParams.append('course_id', courseId);
      }
      
      if (deviceType && deviceType !== 'All') {
        queryParams.append('device_type', deviceType);
      }
      
      if (entityType && entityType !== 'All') {
        queryParams.append('entity_type', entityType);
      }
      
      const url = `${this.baseUrl}/cop-optimized/device-status${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('[COP API] Error fetching device status:', error);
      throw error;
    }
  }





}

export const copApiService = new COPApiService();
